import React from 'react';

import { fmtDateTime, fmtQty } from '../format';
import {
  GOODS_RECEIPT_STATUS,
  HUB_TRANSFER_STATUS,
  metaOf,
  OUTBOUND_STATUS,
  PO_STATUS,
  GOODS_ISSUE_STATUS,
  OPNAME_STATUS,
  RECEIPT_STATUS,
  TRANSFER_STATUS,
} from '../status';
import type {
  GoodsIssueSummary,
  GoodsReceiptSummary,
  HubTransferEvent,
  HubTransferStatus,
  OutboundOrderEvent,
  PurchaseOrder,
  StockOpnameEvent,
  StockOpnameStatus,
  TransferEvent,
  TransferStatus,
} from '../types';
import { Section, StatusBadge, Timeline, type TimelineEntry } from './ui';

/**
 * Who did what, when — the history of a warehouse document, newest first,
 * like the Shipments detail. The outbound order keeps its own event log;
 * the other documents derive theirs from the actor / timestamp pairs they
 * already carry (created, confirmed, posted, ready, shipped, cancelled).
 */

const EVENT_ICON: Record<string, string> = {
  CLIENT: 'ri-code-s-slash-line',
  FULFILLMENT: 'ri-truck-line',
  WMS: 'ri-time-line',
};

/** Newest first; at the same instant the later step (added later) comes first. */
const byNewest = (entries: TimelineEntry[]): TimelineEntry[] =>
  entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) =>
      a.e.at < b.e.at ? 1 : a.e.at > b.e.at ? -1 : b.i - a.i
    )
    .map(({ e }) => e);

/** The outbound order's event log (server-side), as timeline entries. */
export const outboundOrderHistory = (
  events: OutboundOrderEvent[]
): TimelineEntry[] =>
  events.map((e) => ({
    id: e.id,
    at: e.occurredAt,
    title: e.title,
    note: e.note,
    by: e.actor,
    icon: EVENT_ICON[e.origin] ?? 'ri-time-line',
    badge: e.status ? (
      <StatusBadge meta={metaOf(OUTBOUND_STATUS, e.status)} />
    ) : undefined,
  }));

/** Purchase order / inbound request: draft → confirmed → receipts → finished / cancelled. */
export const purchaseOrderHistory = (
  order: PurchaseOrder,
  receipts: GoodsReceiptSummary[]
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    {
      id: 'created',
      at: order.createdAt,
      title:
        order.type === 'TRANSFER'
          ? 'PO transfer dibuat dari outbound transfer'
          : 'PO dibuat',
      by: order.createdBy,
      icon: 'ri-file-add-line',
      badge: <StatusBadge meta={metaOf(PO_STATUS, 'DRAFT')} />,
    },
  ];
  if (order.confirmedAt) {
    entries.push({
      id: 'confirmed',
      at: order.confirmedAt,
      title: 'PO di-confirm · inbound request terbit',
      by: order.confirmedBy ?? undefined,
      icon: 'ri-checkbox-circle-line',
      badge: <StatusBadge meta={metaOf(PO_STATUS, 'CONFIRMED')} />,
    });
  }
  const posted = receipts
    .filter((r) => r.postedAt)
    .sort((a, b) => (a.postedAt! < b.postedAt! ? -1 : 1));
  posted.forEach((r) => {
    entries.push({
      id: `gr-${r.id}`,
      at: r.postedAt as string,
      title: `Goods receipt ${r.code}`,
      note: [
        `${fmtQty(r.totalQty)} diterima · ${r.lineCount} baris`,
        r.deliveryNoteNo ? `surat jalan ${r.deliveryNoteNo}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      by: r.postedBy ?? r.createdBy,
      icon: 'ri-inbox-archive-line',
      badge: <StatusBadge meta={metaOf(RECEIPT_STATUS, 'PARTIAL_RECEIPT')} />,
    });
  });
  const last = posted[posted.length - 1];
  if (order.receiptStatus === 'FINISHED' && last?.postedAt) {
    entries.push({
      id: 'finished',
      at: last.postedAt,
      title: 'Penerimaan selesai',
      note:
        order.totalReceived < order.totalQty
          ? 'Sisa PO ditutup (cancel backorder)'
          : 'Semua barang diterima',
      by: last.postedBy ?? undefined,
      icon: 'ri-flag-line',
      badge: <StatusBadge meta={metaOf(RECEIPT_STATUS, 'FINISHED')} />,
    });
  }
  if (order.cancelledAt) {
    entries.push({
      id: 'cancelled',
      at: order.cancelledAt,
      title: 'PO dibatalkan',
      note: order.cancellationReason ?? undefined,
      by: order.cancelledBy ?? undefined,
      icon: 'ri-close-circle-line',
      badge: <StatusBadge meta={metaOf(PO_STATUS, 'CANCELLED')} />,
    });
  }
  return byNewest(entries);
};

/** Goods receipt: arrival and posting. */
export const goodsReceiptHistory = (
  receipt: GoodsReceiptSummary,
  closesBackorder: boolean
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [];
  if (receipt.arrivedAt) {
    entries.push({
      id: 'arrived',
      at: receipt.arrivedAt,
      title: 'Barang tiba',
      note: receipt.deliveryNoteNo
        ? `Surat jalan ${receipt.deliveryNoteNo}`
        : undefined,
      icon: 'ri-truck-line',
    });
  }
  entries.push({
    id: 'posted',
    at: receipt.postedAt ?? receipt.createdAt,
    title: receipt.postedAt ? 'Goods receipt di-post' : 'Goods receipt dibuat',
    note: [
      `${fmtQty(receipt.totalQty)} masuk stok · ${receipt.lineCount} baris`,
      closesBackorder ? 'sisa PO ditutup' : null,
    ]
      .filter(Boolean)
      .join(' · '),
    by: receipt.postedBy ?? receipt.createdBy,
    icon: 'ri-inbox-archive-line',
    badge: <StatusBadge meta={metaOf(GOODS_RECEIPT_STATUS, receipt.status)} />,
  });
  return byNewest(entries);
};

/** A goods issue on its own (no outbound order): the picking / shipping steps it carries. */
export const goodsIssueHistory = (gi: GoodsIssueSummary): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    {
      id: 'created',
      at: gi.createdAt,
      title: 'Picking order dibuat',
      by: gi.createdBy,
      icon: 'ri-file-list-3-line',
      badge: <StatusBadge meta={metaOf(GOODS_ISSUE_STATUS, 'RESERVED')} />,
    },
  ];
  if (gi.pickedAt)
    entries.push({
      id: 'picked',
      at: gi.pickedAt,
      title: 'Barang diambil',
      by: gi.pickerName ?? undefined,
      icon: 'ri-box-3-line',
      badge: <StatusBadge meta={metaOf(GOODS_ISSUE_STATUS, 'PICKED')} />,
    });
  if (gi.readyAt)
    entries.push({
      id: 'ready',
      at: gi.readyAt,
      title: 'Ready to pick up',
      note: gi.suratJalanNo ? `Surat jalan ${gi.suratJalanNo}` : undefined,
      by: gi.readyBy ?? undefined,
      icon: 'ri-checkbox-circle-line',
      badge: <StatusBadge meta={metaOf(GOODS_ISSUE_STATUS, 'READY_TO_PICKUP')} />,
    });
  if (gi.shippedAt)
    entries.push({
      id: 'shipped',
      at: gi.shippedAt,
      title: 'Shipping di-submit',
      note: gi.waybill ? `Waybill ${gi.waybill}` : undefined,
      by: gi.shippedBy ?? undefined,
      icon: 'ri-send-plane-line',
      badge: <StatusBadge meta={metaOf(GOODS_ISSUE_STATUS, 'SHIPPED')} />,
    });
  if (gi.cancelledAt)
    entries.push({
      id: 'cancelled',
      at: gi.cancelledAt,
      title: 'Picking dihapus',
      icon: 'ri-close-circle-line',
    });
  return byNewest(entries);
};

/* transfer storage */

const TRANSFER_EVENT_ICON: Record<TransferStatus, string> = {
  REQUESTED: 'ri-file-add-line',
  APPROVED: 'ri-checkbox-circle-line',
  PREPARING: 'ri-box-3-line',
  DONE: 'ri-flag-line',
  REJECTED: 'ri-close-circle-line',
  CANCELLED: 'ri-forbid-line',
};

/**
 * A transfer's event log (server-side, newest first) as timeline entries.
 * Re-sorted defensively; reversed first so events at the same instant keep
 * the server's order (byNewest puts the later index first).
 */
export const transferHistory = (events: TransferEvent[]): TimelineEntry[] =>
  byNewest(
    [...events].reverse().map((e) => ({
      id: e.id,
      at: e.occurredAt,
      title: e.title,
      note: e.note || undefined,
      by: e.actor,
      icon: (e.status && TRANSFER_EVENT_ICON[e.status]) || 'ri-time-line',
      badge: e.status ? (
        <StatusBadge meta={metaOf(TRANSFER_STATUS, e.status)} />
      ) : undefined,
    }))
  );

/* transfer warehouse (hub → hub) */

const HUB_TRANSFER_EVENT_ICON: Record<HubTransferStatus, string> = {
  REQUESTED: 'ri-file-add-line',
  FIRST_APPROVED: 'ri-checkbox-circle-line',
  APPROVED: 'ri-checkbox-multiple-line',
  PREPARING: 'ri-box-3-line',
  READY_TO_PICKUP: 'ri-hand-coin-line',
  SHIPPED: 'ri-truck-line',
  PARTIAL_RECEIPT: 'ri-inbox-unarchive-line',
  RECEIVED: 'ri-flag-line',
  REJECTED: 'ri-close-circle-line',
  CANCELLED: 'ri-forbid-line',
};

/** A hub transfer's event log (server-side, newest first) as timeline entries. */
export const hubTransferHistory = (
  events: HubTransferEvent[]
): TimelineEntry[] =>
  byNewest(
    [...events].reverse().map((e) => ({
      id: e.id,
      at: e.occurredAt,
      title: e.title,
      note: e.note || undefined,
      by: e.actor,
      icon: (e.status && HUB_TRANSFER_EVENT_ICON[e.status]) || 'ri-time-line',
      badge: e.status ? (
        <StatusBadge meta={metaOf(HUB_TRANSFER_STATUS, e.status)} />
      ) : undefined,
    }))
  );

/* stock opname */

const OPNAME_EVENT_ICON: Record<StockOpnameStatus, string> = {
  WAITING_MANAGER_APPROVAL: 'ri-file-add-line',
  WAITING_FAT_APPROVAL: 'ri-checkbox-circle-line',
  DONE: 'ri-flag-line',
  REJECTED_BY_MANAGER: 'ri-close-circle-line',
  REJECTED_BY_FAT: 'ri-close-circle-line',
  CANCELLED: 'ri-forbid-line',
};

/** An opname's event log (server-side, newest first) as timeline entries. */
export const opnameHistory = (events: StockOpnameEvent[]): TimelineEntry[] =>
  byNewest(
    [...events].reverse().map((e) => ({
      id: e.id,
      at: e.occurredAt,
      title: e.title,
      note: e.note || undefined,
      by: e.actor,
      icon: (e.status && OPNAME_EVENT_ICON[e.status]) || 'ri-time-line',
      badge: e.status ? (
        <StatusBadge meta={metaOf(OPNAME_STATUS, e.status)} />
      ) : undefined,
    }))
  );

/** The "Timeline" card every detail page shows. */
export const HistorySection: React.FC<{
  entries: TimelineEntry[];
  loading?: boolean;
}> = ({ entries, loading }) => (
  <Section title="Timeline">
    <div data-testid="history-timeline">
      {loading && !entries.length ? (
        <p className="text-[12px] text-text-sub-600">Memuat…</p>
      ) : (
        <Timeline entries={entries} formatAt={fmtDateTime} />
      )}
    </div>
  </Section>
);
